import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import config from '../config';
import SectionReveal from '../components/SectionReveal';
import './ProductDetail.css';

// Collect all plans from all categories with their source info
function getAllPlans() {
  const plans = [];
  config.corporatePlans.forEach((p) => plans.push({ ...p, category: 'corporate', categoryLabel: 'Corporate Wellness', backUrl: '/corporate' }));
  config.gymPlans.forEach((p) => plans.push({ ...p, category: 'gym', categoryLabel: 'Gym & Fitness', backUrl: '/gym-chains' }));
  config.societyPlans.forEach((p) => plans.push({ ...p, category: 'society', categoryLabel: 'Society & Community', backUrl: '/societies' }));
  return plans;
}

function PlanDetail() {
  const { id } = useParams();
  const allPlans = getAllPlans();
  const plan = allPlans.find((p) => p.id === id);

  const [quantity, setQuantity] = useState(1);
  const [pincode, setPincode] = useState('');
  const [pincodeValid, setPincodeValid] = useState(null);

  if (!plan) {
    return (
      <main className="product-detail-page">
        <div className="container" style={{ padding: '160px 24px 80px', textAlign: 'center' }}>
          <h2>Plan not found</h2>
          <Link to="/" className="btn btn-primary" style={{ marginTop: '20px' }}>
            Back to Home
          </Link>
        </div>
      </main>
    );
  }

  const isFreeDelivery = quantity >= config.delivery.freeDeliveryMinBowls;
  const deliveryCharge = isFreeDelivery ? 0 : config.delivery.deliveryCharge;
  const totalPrice = plan.monthlyPrice * quantity + deliveryCharge;

  const handleCheckPincode = () => {
    const isValid = config.serviceablePincodes.includes(pincode.trim());
    setPincodeValid(isValid);
  };

  const handleOrder = () => {
    const deliveryText = deliveryCharge > 0
      ? `🚚 Delivery Charge: ₹${deliveryCharge}`
      : `🚚 Delivery: FREE`;
    const message = `Hello FAL SUTRA! 👋\n\nI want to subscribe (${plan.categoryLabel}):\n📋 Plan: ${plan.header}\n📦 Quantity: ${quantity} subscription(s)\n💰 Plan Price: ₹${plan.monthlyPrice.toLocaleString('en-IN')}/month each\n${deliveryText}\n💵 Total Amount: ₹${totalPrice.toLocaleString('en-IN')}\n📍 Delivery Pincode: ${pincode || 'N/A'}\n\nPlease confirm my subscription!`;
    const url = config.getWhatsAppUrl(message);
    window.open(url, '_blank');
  };

  return (
    <main className="product-detail-page">
      <section className="product-detail-hero">
        <div className="container">
          <Link to={plan.backUrl} className="product-detail__back">
            ← Back to {plan.categoryLabel} Plans
          </Link>
        </div>
      </section>

      <section className="section product-detail-section">
        <div className="container">
          <div className="product-detail">
            {/* Left Side - Image */}
            <SectionReveal animation="slideInLeft">
              <div className="product-detail__image-wrap">
                <img src={plan.image} alt={plan.header} className="product-detail__image" />
              </div>
            </SectionReveal>

            {/* Right Side - Details */}
            <SectionReveal animation="slideInRight" delay={0.15}>
              <div className="product-detail__info">
                <div className="product-detail__type-badge" style={{ background: plan.color }}>
                  {plan.categoryLabel}
                </div>
                <h1 className="product-detail__name">{plan.header}</h1>

                <div className="product-detail__plan-details">
                  <div className="product-detail__plan-row">
                    <span className="product-detail__plan-label">Monthly Price</span>
                    <span className="product-detail__plan-value product-detail__plan-value--price">
                      ₹{plan.monthlyPrice.toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div className="product-detail__plan-row">
                    <span className="product-detail__plan-label">Price per Cup</span>
                    <span className="product-detail__plan-value">₹{plan.sellingPricePerCup}</span>
                  </div>
                  <div className="product-detail__plan-row">
                    <span className="product-detail__plan-label">Plan Duration</span>
                    <span className="product-detail__plan-value">{plan.planDurationDays} Days (Mon-Sat)</span>
                  </div>
                </div>

                {plan.ingredients && (
                  <div className="product-detail__ingredients">
                    <span className="product-detail__ingredients-label">Ingredients:</span>
                    <div className="product-detail__ingredient-tags">
                      {plan.ingredients.map((ing, i) => (
                        <span key={i} className="product-detail__ingredient-tag">{ing}</span>
                      ))}
                    </div>
                    {plan.toppings && <p className="product-detail__toppings">{plan.toppings}</p>}
                  </div>
                )}

                {plan.bestFor && (
                  <div className="product-detail__best-for">
                    <strong>Best For:</strong> {plan.bestFor}
                  </div>
                )}

                {/* Quantity Selector */}
                <div className="product-detail__quantity-section">
                  <label className="product-detail__qty-label">Subscriptions:</label>
                  <div className="product-detail__quantity-controls">
                    <button onClick={() => setQuantity(Math.max(1, quantity - 1))}>−</button>
                    <span className="product-detail__quantity-value">{quantity}</span>
                    <button onClick={() => setQuantity(quantity + 1)}>+</button>
                  </div>
                </div>

                {/* Delivery Info */}
                <div className="product-detail__delivery-info">
                  {isFreeDelivery ? (
                    <span className="product-detail__free-delivery">✅ Free Delivery (3+ subscriptions)</span>
                  ) : (
                    <span className="product-detail__delivery-charge">
                      🚚 Delivery Charge: ₹{config.delivery.deliveryCharge} (Free on 3+ subscriptions)
                    </span>
                  )}
                </div>

                {/* Total */}
                <div className="product-detail__total-row">
                  <span>Total Amount:</span>
                  <span className="product-detail__total-price">₹{totalPrice.toLocaleString('en-IN')}</span>
                </div>

                {/* Pincode Check */}
                {pincodeValid === null && (
                  <div className="product-detail__pincode-check">
                    <p className="product-detail__pincode-label">Check delivery availability:</p>
                    <div className="product-detail__pincode-row">
                      <input
                        type="text"
                        value={pincode}
                        onChange={(e) => {
                          setPincode(e.target.value.replace(/\D/g, '').slice(0, 6));
                          setPincodeValid(null);
                        }}
                        placeholder="Enter pincode e.g. 380054"
                        className="product-detail__pincode-input"
                        maxLength={6}
                      />
                      <button
                        onClick={handleCheckPincode}
                        className="btn btn-dark product-detail__pincode-btn"
                        disabled={pincode.length !== 6}
                      >
                        Check
                      </button>
                    </div>
                  </div>
                )}

                {pincodeValid === false && (
                  <div className="product-detail__not-serviceable">
                    <p>❌ Currently we don't serve your area.</p>
                    <p className="product-detail__not-serviceable-sub">
                      We deliver within {config.delivery.radiusKm} KM of {config.delivery.baseArea}
                    </p>
                    <button
                      className="product-detail__retry-btn"
                      onClick={() => { setPincode(''); setPincodeValid(null); }}
                    >
                      Try another pincode
                    </button>
                  </div>
                )}

                {pincodeValid === true && (
                  <div className="product-detail__serviceable">
                    <p>✅ Great! We deliver to {pincode}</p>
                  </div>
                )}

                {/* Order Button */}
                <div className="product-detail__actions">
                  <button
                    className="btn btn-whatsapp product-detail__order-btn"
                    onClick={handleOrder}
                  >
                    📱 Order on WhatsApp
                  </button>
                </div>
              </div>
            </SectionReveal>
          </div>

          {/* Description Section Below */}
          <SectionReveal delay={0.3}>
            <div className="product-detail__description-section">
              <h2>About This Plan</h2>
              <p>{plan.description}</p>
            </div>
          </SectionReveal>
        </div>
      </section>
    </main>
  );
}

export default PlanDetail;
